import { useMemo } from 'react';

import useUserState from '../../../../hooks/useUserState';

/**
 * Claves de permisos granulares del asignatura (HU 15447).
 *
 * Reemplazan al legado `academic-offer.curricular-subject.all` para poder
 * distinguir lectura / escritura y ámbito administrativo / docente.
 */
const CURRICULAR_SUBJECT_PERMISSION_KEYS = {
  adminRead: 'academic-offer.curricular-subject.administrative.read',
  adminWrite: 'academic-offer.curricular-subject.administrative.write',
  teachingRead: 'academic-offer.curricular-subject.teaching.read',
  teachingWrite: 'academic-offer.curricular-subject.teaching.write',
} as const;

export interface CourseTabPermissions {
  /** true si el usuario tiene admin.read O admin.write (write implica read). */
  canReadAdministrative: boolean;
  /** true si el usuario tiene admin.write. */
  canWriteAdministrative: boolean;
  /** true si el usuario tiene teaching.read O teaching.write (write implica read). */
  canReadTeaching: boolean;
  /** true si el usuario tiene teaching.write. */
  canWriteTeaching: boolean;
}

/**
 * Hook que expone los 4 flags de permiso granular para el formulario de
 * asignatura (CourseForm). Encapsula la lógica de "write implica read".
 *
 * Uso:
 *   const { canReadAdministrative, canWriteTeaching, ... } = useCourseTabPermissions();
 */
export function useCourseTabPermissions(): CourseTabPermissions {
  const { isAuthorizedTo } = useUserState();

  return useMemo(
    () => ({
      canReadAdministrative: isAuthorizedTo([
        CURRICULAR_SUBJECT_PERMISSION_KEYS.adminRead,
        CURRICULAR_SUBJECT_PERMISSION_KEYS.adminWrite,
      ]),
      canWriteAdministrative: isAuthorizedTo([
        CURRICULAR_SUBJECT_PERMISSION_KEYS.adminWrite,
      ]),
      canReadTeaching: isAuthorizedTo([
        CURRICULAR_SUBJECT_PERMISSION_KEYS.teachingRead,
        CURRICULAR_SUBJECT_PERMISSION_KEYS.teachingWrite,
      ]),
      canWriteTeaching: isAuthorizedTo([
        CURRICULAR_SUBJECT_PERMISSION_KEYS.teachingWrite,
      ]),
    }),
    [isAuthorizedTo],
  );
}

export default useCourseTabPermissions;
